import { Collapse, Stack } from '@mui/material';

import {
  Typography,
  Paper,
  Divider,
  Switches,
  CardContent,
  CardHeader,
  GroupOutlinedIcon,
  IconAvatar,
} from '@occc-app/design-system';
import {
  GlobalFulfillmentLimit,
  LimitComponentProps,
  LimitsInConstraintGroupFormValue,
  periodOptionsExcludingTotal,
  PerMemberFulfillmentLimit,
  PerTransactionFulfillmentLimit,
  ProgressPerPeriodLimit,
  ProgressPerTransactionLimit,
  TransactionFrequencyLimit,
  type TransactionFrequencyTimePeriod,
} from '@occc/feat-limits';

type AwardCountProps = {
  title?: string;
  description?: string;
  values: {
    awardCount: LimitsInConstraintGroupFormValue;
  };
  perMemberFulfillmentLimitProps?: Omit<LimitComponentProps, 'open'>;
  globalFulfillmentLimitProps?: Omit<LimitComponentProps, 'open'>;
  shouldShowTransactionLimit?: boolean;
  shouldShowProgressPerPeriodLimit?: boolean;
  shouldShowProgressPerTransactionLimit?: boolean;
  shouldShowTransactionFrequencyLimit?: boolean;
  transactionFrequencyExcludeTotal?: boolean;
  defaultPeriod?: TransactionFrequencyTimePeriod;
  stacked?: boolean;
};

export const AwardCount = ({
  title,
  description,
  values,
  perMemberFulfillmentLimitProps,
  globalFulfillmentLimitProps,
  shouldShowTransactionLimit = false,
  shouldShowProgressPerPeriodLimit = false,
  shouldShowProgressPerTransactionLimit = false,
  shouldShowTransactionFrequencyLimit = false,
  transactionFrequencyExcludeTotal = false,
  defaultPeriod: defaultPeriodProp,
  stacked = false,
}: AwardCountProps) => (
  <Paper>
    <CardHeader
      title={<Typography variant='subtitle1'>{title || 'Set Fulfillment Limits'}</Typography>}
      subheader={
        <Typography variant='body2' color='textSecondary'>
          {description ? description : 'Limits the number of fulfillments'}
        </Typography>
      }
      action={<Switches name='awardCount.isEnabled' data={{}} />}
      avatar={<IconAvatar Icon={GroupOutlinedIcon} />}
      sx={{ py: 2, px: 3 }}
    />
    <Collapse in={values?.awardCount?.isEnabled} timeout='auto' unmountOnExit>
      <CardContent sx={{ pt: 0 }}>
        <Stack component={Paper} variant='outlined' divider={<Divider />}>
          <PerMemberFulfillmentLimit
            {...perMemberFulfillmentLimitProps}
            stacked={stacked}
            open={
              stacked
                ? values?.awardCount?.membershipAwardCountLimits?.isEnabled ?? false
                : values?.awardCount?.membership?.isEnabled ?? false
            }
          />

          <GlobalFulfillmentLimit
            {...globalFulfillmentLimitProps}
            stacked={stacked}
            open={
              stacked
                ? values?.awardCount?.globalAwardCountLimits?.isEnabled ?? false
                : values?.awardCount?.global?.isEnabled ?? false
            }
          />

          {shouldShowTransactionLimit && (
            <PerTransactionFulfillmentLimit
              open={values.awardCount.transaction?.isEnabled ?? false}
            />
          )}

          {shouldShowProgressPerPeriodLimit && (
            <ProgressPerPeriodLimit
              open={values.awardCount.progressPerPeriod?.isEnabled ?? false}
            />
          )}

          {shouldShowProgressPerTransactionLimit && (
            <ProgressPerTransactionLimit
              open={values.awardCount.progressPerTransaction?.isEnabled ?? false}
            />
          )}

          {shouldShowTransactionFrequencyLimit && (
            <TransactionFrequencyLimit
              open={values.awardCount.transactionFrequency?.isEnabled ?? false}
              periodOptionsToUse={
                transactionFrequencyExcludeTotal ? periodOptionsExcludingTotal : undefined
              }
              defaultPeriod={defaultPeriodProp ?? 'DAY'}
            />
          )}
        </Stack>
      </CardContent>
    </Collapse>
  </Paper>
);
